import { useEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGlobalFilter, TIME_RANGE_LABELS, type GlobalTimeRange } from '@/contexts/GlobalFilterContext';

const TIME_OPTIONS: { key: GlobalTimeRange; label: string }[] = [
  { key: 'today', label: '今日' },
  { key: 'yesterday', label: '昨日' },
  { key: 'week', label: '本周' },
  { key: 'month', label: '本月' },
  { key: 'quarter', label: '本季度' },
  { key: 'year', label: '本年' },
  { key: 'custom', label: '自定义' },
];

/**
 * 移动端全局时间筛选入口（<1280px 顶部条使用）。
 * TopBar 在 <1280px 整体隐藏，导致手机端无法使用全局时间筛选；
 * 此组件以紧凑下拉形式提供同样的时间范围切换能力。
 */
export function MobileTimeFilter() {
  const {
    timeRange, setTimeRange,
    customStartDate, setCustomStartDate,
    customEndDate, setCustomEndDate,
  } = useGlobalFilter();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const currentLabel = timeRange === 'custom' && (customStartDate || customEndDate)
    ? `${customStartDate || '开始'}~${customEndDate || '止'}`
    : TIME_RANGE_LABELS[timeRange];

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1 h-9 px-2.5 rounded-lg border text-[12px] font-medium shadow-sm transition-colors',
          open
            ? 'bg-emerald-600 border-emerald-600 text-white'
            : 'bg-white border-zinc-200 text-emerald-700 hover:bg-emerald-50'
        )}
        aria-label="时间筛选"
      >
        <Calendar className="w-4 h-4" />
        <span className="max-w-[96px] truncate">{currentLabel}</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-zinc-200 rounded-xl shadow-xl z-[130] w-[220px] p-1.5">
          <div className="grid grid-cols-2 gap-1">
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setTimeRange(opt.key)}
                className={cn(
                  'px-2 py-2 text-[12.5px] rounded-lg transition-colors text-center',
                  timeRange === opt.key
                    ? 'bg-emerald-600 text-white font-medium'
                    : 'text-zinc-600 hover:bg-emerald-50 hover:text-emerald-700'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {timeRange === 'custom' && (
            <div className="px-1.5 pt-2 pb-1 border-t border-zinc-100 mt-1 flex flex-col gap-1.5">
              <label className="text-[11px] text-zinc-500">开始日期</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => { setCustomStartDate(e.target.value); if (!customEndDate) setCustomEndDate(e.target.value); }}
                className="w-full h-9 px-2 text-[12.5px] border border-zinc-200 rounded-lg outline-none focus:border-emerald-500 text-zinc-700"
              />
              <label className="text-[11px] text-zinc-500">结束日期</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full h-9 px-2 text-[12.5px] border border-zinc-200 rounded-lg outline-none focus:border-emerald-500 text-zinc-700"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
